/**
 * Analytics Service for Wildlife Migration Tracker
 * Tracks usage events and stores them in localStorage
 */

const STORAGE_KEY = 'wmt_analytics';
const EVENT_RETENTION_DAYS = 90;

/**
 * Valid event types for tracking
 */
const EVENT_TYPES = [
  'mapbox_load',
  'page_view',
  'species_select',
  'category_filter',
  'map_zoom',
  'map_fullscreen',
  'session_start',
  'session_end'
];

/**
 * Generate a unique session ID
 * @returns {string} Unique session identifier
 */
function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Get the current session ID or create a new one
 * @returns {string} Current session ID
 */
function getSessionId() {
  let sessionId = sessionStorage.getItem('wmt_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('wmt_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Load events from localStorage
 * @returns {Array} Array of stored events
 */
function loadEvents() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to load analytics events:', error);
    return [];
  }
}

/**
 * Save events to localStorage
 * @param {Array} events - Events to save
 */
function saveEvents(events) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch (error) {
    console.error('Failed to save analytics events:', error);
  }
}

/**
 * Track an analytics event
 * @param {string} eventName - Name of the event (must be a valid EVENT_TYPE)
 * @param {Object} [data={}] - Additional event data
 * @returns {Object|null} The tracked event or null if invalid
 */
export function track(eventName, data = {}) {
  if (!EVENT_TYPES.includes(eventName)) {
    console.warn(`Invalid event type: ${eventName}`);
    return null;
  }

  const event = {
    id: `evt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    type: eventName,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    data
  };

  const events = loadEvents();
  events.push(event);
  saveEvents(events);

  return event;
}

/**
 * Get events with optional filters
 * @param {Object} [filters={}] - Filter options
 * @param {string} [filters.type] - Filter by event type
 * @param {string|Date} [filters.startDate] - Filter events after this date
 * @param {string|Date} [filters.endDate] - Filter events before this date
 * @param {string} [filters.sessionId] - Filter by session ID
 * @returns {Array} Filtered events
 */
export function getEvents(filters = {}) {
  let events = loadEvents();

  if (filters.type) {
    events = events.filter(e => e.type === filters.type);
  }

  if (filters.startDate) {
    const start = new Date(filters.startDate);
    events = events.filter(e => new Date(e.timestamp) >= start);
  }

  if (filters.endDate) {
    const end = new Date(filters.endDate);
    events = events.filter(e => new Date(e.timestamp) <= end);
  }

  if (filters.sessionId) {
    events = events.filter(e => e.sessionId === filters.sessionId);
  }

  return events;
}

/**
 * Get Mapbox load count for a specific month
 * @param {string} [month] - Month in YYYY-MM format (defaults to current month)
 * @returns {Object} Mapbox usage statistics
 */
export function getMapboxUsage(month) {
  const targetMonth = month || new Date().toISOString().substring(0, 7);
  const [year, monthNum] = targetMonth.split('-').map(Number);

  const startDate = new Date(year, monthNum - 1, 1);
  const endDate = new Date(year, monthNum, 0, 23, 59, 59);

  const mapboxEvents = getEvents({
    type: 'mapbox_load',
    startDate,
    endDate
  });

  const uniqueSessions = new Set(mapboxEvents.map(e => e.sessionId));

  return {
    month: targetMonth,
    totalLoads: mapboxEvents.length,
    uniqueSessions: uniqueSessions.size,
    events: mapboxEvents
  };
}

/**
 * Get aggregated metrics from all tracked events
 * @returns {Object} Aggregated metrics
 */
export function getMetrics() {
  const events = loadEvents();

  const metrics = {
    totalEvents: events.length,
    totalPageViews: 0,
    totalSessions: 0,
    topSpecies: [],
    topCategories: [],
    eventsByType: {},
    mapboxLoadsThisMonth: 0
  };

  if (events.length === 0) {
    return metrics;
  }

  // Count events by type
  const speciesCount = {};
  const categoryCount = {};
  const sessions = new Set();

  events.forEach(event => {
    // Count by type
    metrics.eventsByType[event.type] = (metrics.eventsByType[event.type] || 0) + 1;

    // Track sessions
    sessions.add(event.sessionId);

    // Count species selections
    if (event.type === 'species_select' && event.data?.species) {
      speciesCount[event.data.species] = (speciesCount[event.data.species] || 0) + 1;
    }

    // Count category filters
    if (event.type === 'category_filter' && event.data?.category) {
      categoryCount[event.data.category] = (categoryCount[event.data.category] || 0) + 1;
    }
  });

  metrics.totalPageViews = metrics.eventsByType['page_view'] || 0;
  metrics.totalSessions = sessions.size;

  // Get top species (sorted by count)
  metrics.topSpecies = Object.entries(speciesCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([species, count]) => ({ species, count }));

  // Get top categories (sorted by count)
  metrics.topCategories = Object.entries(categoryCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([category, count]) => ({ category, count }));

  // Mapbox loads this month
  metrics.mapboxLoadsThisMonth = getMapboxUsage().totalLoads;

  return metrics;
}

/**
 * Remove events older than 90 days
 * @returns {number} Number of events removed
 */
export function clearOldEvents() {
  const events = loadEvents();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - EVENT_RETENTION_DAYS);

  const filteredEvents = events.filter(e => new Date(e.timestamp) >= cutoffDate);
  const removedCount = events.length - filteredEvents.length;

  saveEvents(filteredEvents);

  return removedCount;
}

/**
 * Export analytics data
 * @param {string} [format='json'] - Export format (currently only 'json' supported)
 * @returns {string} Exported data as string
 */
export function exportData(format = 'json') {
  const data = {
    exportedAt: new Date().toISOString(),
    metrics: getMetrics(),
    events: loadEvents()
  };

  if (format === 'json') {
    return JSON.stringify(data, null, 2);
  }

  // Default to JSON
  return JSON.stringify(data, null, 2);
}

/**
 * Initialize analytics - tracks session start
 * Should be called when the app loads
 */
export function initAnalytics() {
  // Clear old events on init
  clearOldEvents();

  // Track session start
  track('session_start', {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    url: typeof window !== 'undefined' ? window.location.href : 'unknown'
  });
}

/**
 * End session tracking
 * Should be called when the app unloads
 */
export function endSession() {
  track('session_end', {
    duration: getSessionDuration()
  });
}

/**
 * Get current session duration in seconds
 * @returns {number} Session duration in seconds
 */
function getSessionDuration() {
  const sessionEvents = getEvents({ sessionId: getSessionId(), type: 'session_start' });
  if (sessionEvents.length === 0) return 0;

  const startTime = new Date(sessionEvents[0].timestamp);
  return Math.floor((Date.now() - startTime.getTime()) / 1000);
}

// Export constants for external use
export { EVENT_TYPES, STORAGE_KEY };
